import { z } from "zod";
import {
  ADMIN_ASSIGNABLE_STATUSES,
  AUDIENCE_TYPES,
  DELIVERY_PREFERENCES,
  SERVICE_INTERESTS,
  SUBMISSION_STATUSES,
} from "./domain/submission";
import { CONTACT_MESSAGE_STATUSES } from "./domain/contact";
import { LANGUAGE_TAGS, USER_ROLES } from "./domain/user";
import { VIDEO_SOURCES } from "./domain/video";
import { validationFailed } from "./domain/errors";

/**
 * Field length ceilings. Shared by the API and the frontend so a form can
 * never present a limit the server will reject.
 */
export const FIELD_LIMITS = {
  nameMax: 120,
  emailMax: 254,
  organizationMax: 160,
  messageMin: 10,
  messageMax: 4000,
  noteMax: 4000,
  phoneMax: 40,
} as const;

const trimmed = (max: number) => z.string().trim().max(max);

/**
 * Email validation is deliberately permissive on shape and strict on length.
 * Deliverability is proven by the verification email, not by a regex.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(FIELD_LIMITS.emailMax)
  .email();

// Each schema is built from the domain tuple, so adding a value in one place
// updates the type, the runtime list and the API contract together.
export const languageSchema = z.enum(LANGUAGE_TAGS);
export const serviceInterestSchema = z.enum(SERVICE_INTERESTS);
export const audienceTypeSchema = z.enum(AUDIENCE_TYPES);
export const deliveryPreferenceSchema = z.enum(DELIVERY_PREFERENCES);
export const submissionStatusSchema = z.enum(SUBMISSION_STATUSES);
export const adminAssignableStatusSchema = z.enum(ADMIN_ASSIGNABLE_STATUSES);
export const contactMessageStatusSchema = z.enum(CONTACT_MESSAGE_STATUSES);
export const videoSourceSchema = z.enum(VIDEO_SOURCES);
export const userRoleSchema = z.enum(USER_ROLES);

/** Client-generated idempotency key. Opaque to the server beyond its shape. */
export const clientRequestIdSchema = z
  .string()
  .trim()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, "clientRequestId must be URL-safe");

export const applicationDataSchema = z.object({
  fullName: trimmed(FIELD_LIMITS.nameMax).min(1),
  contactEmail: emailSchema,
  audienceType: audienceTypeSchema,
  serviceInterest: serviceInterestSchema,
  deliveryPreference: deliveryPreferenceSchema,
  organization: trimmed(FIELD_LIMITS.organizationMax).nullable().default(null),
  message: trimmed(FIELD_LIMITS.messageMax).min(FIELD_LIMITS.messageMin),
  consentToContact: z.boolean(),
  consentToPrivacyPolicy: z.boolean(),
  locale: languageSchema,
});

/**
 * A draft may be saved half-finished, so consent and message length are not
 * enforced until submit. Only the submit path requires the full schema.
 */
export const applicationDraftSchema = applicationDataSchema.extend({
  fullName: trimmed(FIELD_LIMITS.nameMax),
  contactEmail: z.union([emailSchema, z.literal("")]),
  message: trimmed(FIELD_LIMITS.messageMax),
  consentToContact: z.boolean().default(false),
  consentToPrivacyPolicy: z.boolean().default(false),
});

/** Both consents must be affirmatively true at submit time. */
export const submittableApplicationSchema = applicationDataSchema
  .refine((d) => d.consentToContact, {
    path: ["consentToContact"],
    message: "consent_to_contact_required",
  })
  .refine((d) => d.consentToPrivacyPolicy, {
    path: ["consentToPrivacyPolicy"],
    message: "consent_to_privacy_required",
  });

export const contactFormSchema = z.object({
  name: trimmed(FIELD_LIMITS.nameMax).min(1),
  email: emailSchema,
  serviceInterest: serviceInterestSchema,
  audienceType: audienceTypeSchema.nullable().default(null),
  organization: trimmed(FIELD_LIMITS.organizationMax).nullable().default(null),
  message: trimmed(FIELD_LIMITS.messageMax).min(FIELD_LIMITS.messageMin),
  locale: languageSchema,
  consentToContact: z.literal(true, {
    errorMap: () => ({ message: "consent_to_contact_required" }),
  }),
  clientRequestId: clientRequestIdSchema,
  antiAbuseToken: z.string().max(4096).nullable().default(null),
  /**
   * Honeypot. Legitimate browsers leave it empty because it is visually
   * hidden and marked aria-hidden; bots that fill every input trip it.
   */
  website: z.string().max(200).optional(),
});

export const profilePatchSchema = z
  .object({
    displayName: trimmed(FIELD_LIMITS.nameMax).min(1).optional(),
    organization: trimmed(FIELD_LIMITS.organizationMax).nullable().optional(),
    contactPhone: trimmed(FIELD_LIMITS.phoneMax).nullable().optional(),
    preferredLanguage: languageSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "empty_patch" });

export const uploadIntentSchema = z.object({
  submissionId: z.string().trim().min(1).max(64).nullable().default(null),
  /** The MIME type MediaRecorder actually produced, or the picked file's type. */
  mimeType: z.string().trim().min(3).max(120),
  source: videoSourceSchema,
  clientRequestId: clientRequestIdSchema,
  /** Advisory; the authoritative size is read back from storage on complete. */
  expectedSizeBytes: z.number().int().positive().nullable().default(null),
});

export const uploadCompleteSchema = z.object({
  durationMs: z.number().int().nonnegative().nullable().default(null),
  /** Advisory; verified against storage metadata. */
  reportedSizeBytes: z.number().int().nonnegative().nullable().default(null),
});

export const adminStatusPatchSchema = z.object({
  status: adminAssignableStatusSchema,
  note: trimmed(FIELD_LIMITS.noteMax).nullable().default(null),
});

export const adminNoteSchema = z.object({
  body: trimmed(FIELD_LIMITS.noteMax).min(1),
});

export const adminBootstrapSchema = z.object({
  bootstrapSecret: z.string().min(24).max(512),
  email: emailSchema,
});

export const adminRoleSchema = z.object({
  role: userRoleSchema,
});

export const paginationSchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  cursor: z.string().max(512).optional(),
});

export const submissionFilterSchema = paginationSchema.extend({
  status: submissionStatusSchema.optional(),
  email: z.string().trim().toLowerCase().max(FIELD_LIMITS.emailMax).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

export const contactFilterSchema = paginationSchema.extend({
  status: contactMessageStatusSchema.optional(),
  email: z.string().trim().toLowerCase().max(FIELD_LIMITS.emailMax).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

/**
 * Parses with a zod schema and raises the transport-agnostic validation
 * error, flattening issues into a field -> messages map the UI can render
 * next to the offending input in either language.
 */
export const parseOrThrow = <T extends z.ZodTypeAny>(
  schema: T,
  input: unknown,
): z.infer<T> => {
  const result = schema.safeParse(input);
  if (result.success) return result.data;

  const fieldErrors: Record<string, string[]> = {};
  for (const issue of result.error.issues) {
    const key = issue.path.length > 0 ? issue.path.join(".") : "_";
    const existing = fieldErrors[key];
    if (existing) existing.push(issue.message);
    else fieldErrors[key] = [issue.message];
  }
  throw validationFailed({ fieldErrors });
};
